import React, { useState, type MouseEvent } from 'react';
import { parseGenEdEntry, type ParsedGenEdEntry } from '@/utils/genEdEntry';
import {
  GripVertical,
  ChevronDown,
  ChevronRight,
  GraduationCap,
  AlertTriangle,
  RotateCcw,
} from 'lucide-react';
import CourseDropdown from '../components/CourseDropdown';
import CourseRequisites from '../components/CourseRequisites';
import CourseAvailabilityBadge from '../../CourseAvailabilityBadge';
import GradeDistributionChart from '../../GradeDistributionChart';
import { getCourseTypeStyle, getRoleColors, getRequirementPillColors } from '../utils';
import type { UseCourseCardReturn } from '../hooks/useCourseCard';
import { useCreditUnit, useInstitutionConfig } from '../../../../contexts/InstitutionConfigContext';

const REQUIREMENT_NAMES: Record<string, string> = {
  'core-cs': 'Core CS',
  'upper-level-cs': 'Upper CS',
  'cs-area-systems': 'Systems',
  'cs-area-info-proc': 'Info Proc',
  'cs-area-software': 'Software',
  'cs-area-theory': 'Theory',
  'cs-area-numerical': 'Numerical',
  math: 'Math',
  'gen-ed': 'Gen Ed',
};


export type UltraCompactCardProps = UseCourseCardReturn & {
  /** Switch which area an OR gen-ed course satisfies (e.g. DSHU↔DSSP).
   *  Same callback SuggestionCard uses; the backend route handles both
   *  suggestions and planned enrollments. */
  onReassignGenEd?: (courseCode: string, area: string) => void;
};

const UltraCompactCard = ({
  course,
  courseType,
  isDraggable,
  dragBind,
  showActionsMenu,
  toggleDropdown,
  dropdownRef,
  actions,
  onAction,
  className = '',
  isDragging = false,
  requirementTags = [],
  customActions = [],
  semester,
  index,
  checkAvailability,
  showGradeInput = false,
  grade,
  onGradeChange,
  prereqWarning,
  retakeCandidate,
  courseRoles,
  onReassignGenEd,
}: UltraCompactCardProps) => {
  const [showAllTags, setShowAllTags] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const typeStyle = getCourseTypeStyle(courseType);
  // Gate grade-related UI on institution flag (see InstitutionConfigContext).
  const { config: institutionConfig } = useInstitutionConfig();
  const creditUnit = useCreditUnit();
  const showGradeUI = institutionConfig.hasGradeData;

  const handleCardClick = (e: MouseEvent<HTMLDivElement>): void => {
    const target = e.target as Element;
    if (
      target.closest('button') ||
      target.closest('select') ||
      target.closest('[aria-roledescription="draggable"]')
    ) {
      return;
    }
    setIsExpanded(!isExpanded);
  };

  const hasGenEd = !!course.gen_ed_requirements && course.gen_ed_requirements.length > 0;
  const hasGradeRequirement = !!course.grade_required && course.grade_required !== 'D-';

  const genEdEntries: ParsedGenEdEntry[] = hasGenEd
    ? (course.gen_ed_requirements ?? []).map(parseGenEdEntry)
    : [];

  const hasLine2Content =
    !!course.credits ||
    showGradeInput ||
    !!grade ||
    !!course.grade ||
    hasGenEd ||
    !!prereqWarning ||
    !!retakeCandidate ||
    hasGradeRequirement;

  const pillColors = getRequirementPillColors(course, courseRoles);

  const semesterValue = semester;

  const RequirementTags = (): JSX.Element | null => {
    if (!requirementTags || requirementTags.length === 0) return null;

    const visibleTags = showAllTags ? requirementTags : requirementTags.slice(0, 1);
    const hiddenCount = requirementTags.length - 1;

    return (
      <div className="flex items-center gap-1 ml-1 flex-shrink-0">
        {visibleTags.map((tagId, idx) => {
          const tagKey = typeof tagId === 'string' ? tagId : (tagId as { label?: string }).label || '';
          return (
            <span
              key={idx}
              className={`px-2 py-0.5 ${pillColors} text-xs font-medium rounded-full border`}
              title={`Used for: ${REQUIREMENT_NAMES[tagKey] || tagKey}`}
            >
              {REQUIREMENT_NAMES[tagKey] || tagKey}
            </span>
          );
        })}
        {!showAllTags && hiddenCount > 0 && (
          <button
            onMouseEnter={() => setShowAllTags(true)}
            onMouseLeave={() => setShowAllTags(false)}
            onClick={(e) => {
              e.stopPropagation();
              setShowAllTags(!showAllTags);
            }}
            className="px-2 py-0.5 bg-surface-sunken text-ink-2 text-xs font-medium rounded-full border border-line-soft hover:bg-line-soft/60 transition-colors"
            title={requirementTags
              .slice(1)
              .map((id) => {
                const k = typeof id === 'string' ? id : (id as { label?: string }).label || '';
                return REQUIREMENT_NAMES[k] || k;
              })
              .join(', ')}
          >
            +{hiddenCount}
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      <div
        data-course-card
        onClick={handleCardClick}
        onMouseLeave={() => setShowAllTags(false)}
        aria-label={`${course.code}: ${course.title}`}
        aria-expanded={isExpanded}
        className={`
          relative group rounded-card transition-all duration-fast ease-out-soft cursor-pointer
          ${typeStyle}
          ${showActionsMenu ? 'z-50 shadow-md' : 'shadow-sm'}
          ${isDragging ? 'opacity-50 scale-105 shadow-lg ring-2 ring-primary/40' : ''}
          ${isExpanded ? 'ring-2 ring-primary/25' : ''}
          ${className}
        `}
        style={{
          transition: isDragging ? 'none' : undefined,
          padding: isExpanded ? undefined : '0.375rem 0.625rem',
        }}
        title={
          isExpanded ? '' : `${course.code}: ${course.title} (${creditUnit.format(course.credits)})`
        }
      >
        <div className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full overflow-hidden z-10 flex flex-col">
          {(getRoleColors(courseRoles) || ['bg-gray-400']).map((color, i, arr) => (
            <div
              key={i}
              className={color}
              style={{
                flex: 1,
                borderRadius: i === 0 && arr.length === 1 ? '0 0.25rem 0.25rem 0' : undefined,
              }}
            />
          ))}
        </div>

        <div className="flex items-center gap-2 min-w-0 ml-2 min-h-[28px]">
          {isDraggable && dragBind && (
            <div
              ref={dragBind.ref}
              {...dragBind.attributes}
              {...dragBind.listeners}
              className="flex items-center justify-center cursor-grab active:cursor-grabbing flex-shrink-0 p-1 -ml-1 rounded hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-colors touch-none"
              title="Drag to move — or press Space to lift, arrows to move, Space to drop"
            >
              <GripVertical className="w-3 h-3 text-ink-3 group-hover:text-ink-2 transition-colors" />
            </div>
          )}

          <span className="font-semibold text-ink text-sm whitespace-nowrap">
            {course.code}
          </span>

          <span className="text-ink-3/60 text-xs">•</span>

          <span className="text-sm text-ink-2 truncate flex-1">{course.title}</span>

          {prereqWarning && (
            <span
              className="flex items-center gap-0.5 px-2 py-0.5 bg-status-warning/12 text-status-warning-fg text-xs font-medium rounded-full whitespace-nowrap"
              title={`Prereq ${prereqWarning.prereq_code}: earned ${prereqWarning.earned_grade}, need ${prereqWarning.required_grade}`}
            >
              <AlertTriangle className="w-3 h-3" />
              {prereqWarning.prereq_code} {prereqWarning.earned_grade}
            </span>
          )}

          {retakeCandidate && (
            <span
              className={`flex items-center gap-0.5 px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${
                retakeCandidate.reason === 'failing_grade'
                  ? 'bg-status-critical/10 text-status-critical-fg'
                  : retakeCandidate.reason === 'withdrawal'
                    ? 'bg-surface-sunken text-ink-2'
                    : 'bg-status-warning/12 text-status-warning-fg'
              }`}
              title={
                retakeCandidate.reason === 'failing_grade'
                  ? `Grade ${retakeCandidate.earned_grade} — retake recommended`
                  : retakeCandidate.reason === 'withdrawal'
                    ? 'Withdrawn — consider retaking'
                    : `Grade below minimum for: ${retakeCandidate.dependent_courses?.join(', ') || 'dependent courses'}`
              }
            >
              <RotateCcw className="w-3 h-3" />
              Retake
            </span>
          )}

          <RequirementTags />

          <div className="flex-shrink-0 ml-1">
            {isExpanded ? (
              <ChevronDown className="w-3 h-3 text-ink-3" />
            ) : (
              <ChevronRight className="w-3 h-3 text-ink-3" />
            )}
          </div>

          {(actions && actions.length > 0) || (customActions && customActions.length > 0) ? (
            <div className="flex-shrink-0">
              <CourseDropdown
                ref={dropdownRef}
                isOpen={showActionsMenu}
                onToggle={toggleDropdown}
                actions={actions}
                customActions={customActions}
                onAction={onAction ?? (() => {})}
                course={course}
                semester={semester}
              />
            </div>
          ) : null}
        </div>

        {hasLine2Content && (
          <div className="flex flex-wrap items-center gap-2 ml-2 min-w-0 min-h-[22px] mt-0.5">
            <span className="text-xs text-ink-2 tabular-nums whitespace-nowrap flex-shrink-0">
              {creditUnit.format(course.credits)}
            </span>

            {(showGradeInput || grade || course.grade) && (
              <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                {showGradeInput && onGradeChange ? (
                  <select
                    value={grade || course.grade || ''}
                    onChange={(e) => onGradeChange(e.target.value || null)}
                    className="text-xs px-1 py-0 border border-line-soft rounded-control bg-surface-card focus:ring-1 focus:ring-primary focus:border-primary"
                    title="Set grade"
                  >
                    <option value="">Grade</option>
                    <option value="A+">A+</option>
                    <option value="A">A</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B">B</option>
                    <option value="B-">B-</option>
                    <option value="C+">C+</option>
                    <option value="C">C</option>
                    <option value="C-">C-</option>
                    <option value="D+">D+</option>
                    <option value="D">D</option>
                    <option value="D-">D-</option>
                    <option value="F">F</option>
                    <option value="P">P</option>
                    <option value="CR">CR</option>
                    <option value="W">W</option>
                  </select>
                ) : (
                  <span
                    className="px-2 py-0.5 bg-status-completed/15 text-status-completed-fg text-xs font-medium rounded-full"
                    title="Grade earned"
                  >
                    {grade || course.grade}
                  </span>
                )}
              </div>
            )}

            {hasGradeRequirement && (
              <span
                className="px-2 py-0.5 bg-status-progress/12 text-status-progress-fg text-xs font-medium rounded-full whitespace-nowrap flex items-center gap-0.5"
                title="Minimum grade required"
              >
                <GraduationCap className="w-3 h-3" />
                {course.grade_required}
              </span>
            )}

            {showGradeUI && course.grade_data?.distribution && (
              <div
                className="flex h-2 w-8 rounded-sm overflow-hidden flex-shrink-0"
                title={`Avg GPA ${course.grade_data.average_gpa?.toFixed(2) || '?'} • ${course.grade_data.total_students?.toLocaleString() || '?'} students`}
              >
                {[
                  { key: 'A', color: 'bg-green-500' },
                  { key: 'B', color: 'bg-yellow-400' },
                  { key: 'C', color: 'bg-orange-400' },
                  { key: 'D', color: 'bg-orange-600' },
                  { key: 'F', color: 'bg-red-500' },
                  { key: 'W', color: 'bg-gray-400' },
                ].map((g) => {
                  const pct = course.grade_data?.distribution?.[g.key];
                  return pct && pct > 0 ? (
                    <div key={g.key} className={g.color} style={{ width: `${pct}%` }} />
                  ) : null;
                })}
              </div>
            )}

            {hasGenEd && <span className="text-ink-3/60 text-xs">|</span>}

            {genEdEntries.map((entry, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && <span className="text-ink-3/60 text-xs">,</span>}
                {entry.type === 'or' && entry.areas ? (
                  <select
                    // Controlled by the resolved choice from gen_ed_override,
                    // so the select shows the ACTUAL pick (deficit-ranked
                    // default or a prior switch) — not always the first option.
                    value={
                      (course.gen_ed_resolved ?? []).find((a) =>
                        entry.areas?.includes(a)
                      ) ?? entry.areas[0]
                    }
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => onReassignGenEd?.(course.code, e.target.value)}
                    disabled={!onReassignGenEd}
                    // max-w matches the static badge beside it so a long area
                    // code can't push the control past the card edge (`truncate`
                    // is best-effort on <select> across browsers — the width cap
                    // is what's load-bearing; `title` keeps the full text).
                    className="px-1.5 py-0 bg-green-600/10 text-green-700 dark:text-green-300 text-xs rounded-full border border-green-600/20 cursor-pointer hover:bg-green-600/15 focus:outline-none focus:ring-1 focus:ring-green-500 disabled:cursor-default max-w-[8rem] truncate"
                    title={`Choose: ${entry.areas.join(' or ')}`}
                  >
                    {entry.areas.map((area) => (
                      <option key={area} value={area}>
                        {area}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span
                    className="px-1.5 py-0 bg-green-600/10 text-green-700 dark:text-green-300 text-xs rounded-full max-w-[8rem] truncate"
                    title={entry.area}
                  >
                    {entry.area}
                  </span>
                )}
              </React.Fragment>
            ))}

            {semesterValue &&
              semesterValue !== 'bookmarks' &&
              semesterValue !== '000008' &&
              !course.isPlaceholder &&
              checkAvailability && (
                <CourseAvailabilityBadge
                  courseCode={course.code}
                  targetSemester={semesterValue}
                  checkAvailability={checkAvailability as never}
                  compact={true}
                />
              )}
          </div>
        )}

        {isExpanded && (
          <div className="px-3 pb-3 pt-2 ml-2 border-t border-line-soft mt-2">
            <div className="text-sm text-ink font-medium mb-2">{course.title}</div>

            {course.description && (
              <p className="text-xs text-ink-2 mb-2">{course.description}</p>
            )}

            <CourseRequisites
              prerequisites={course.prerequisites_raw}
              corequisites={course.corequisites_raw}
              restriction={course.restriction}
            />

            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <div className="text-ink-2">{creditUnit.heading}:</div>
              <div className="text-ink">{course.credits}</div>

              {hasGenEd && (
                <>
                  <div className="text-ink-2">Gen Ed:</div>
                  <div className="text-green-700 min-w-0 break-words">
                    {(course.gen_ed_requirements ?? []).join(', ')}
                  </div>
                </>
              )}

              {hasGradeRequirement && (
                <>
                  <div className="text-ink-2">Min Grade:</div>
                  <div className="text-ink">{course.grade_required}</div>
                </>
              )}

              {prereqWarning && (
                <>
                  <div className="text-amber-600">Grade Warning:</div>
                  <div className="text-amber-700">
                    {prereqWarning.prereq_code}: earned {prereqWarning.earned_grade}, need{' '}
                    {prereqWarning.required_grade}
                  </div>
                </>
              )}

              {retakeCandidate && (
                <>
                  <div
                    className={
                      retakeCandidate.reason === 'failing_grade'
                        ? 'text-red-600'
                        : 'text-amber-600'
                    }
                  >
                    Retake:
                  </div>
                  <div
                    className={
                      retakeCandidate.reason === 'failing_grade'
                        ? 'text-red-700'
                        : 'text-amber-700'
                    }
                  >
                    {retakeCandidate.reason === 'failing_grade' &&
                      `Grade ${retakeCandidate.earned_grade} — retake recommended`}
                    {retakeCandidate.reason === 'withdrawal' &&
                      'Withdrawn — consider retaking'}
                    {retakeCandidate.reason === 'below_min_grade' &&
                      `Grade ${retakeCandidate.earned_grade} blocks: ${retakeCandidate.dependent_courses?.join(', ')}`}
                  </div>
                </>
              )}

              {showGradeUI && course.grade_data?.difficulty && (
                <>
                  <div className="text-ink-2">Difficulty:</div>
                  <div className="flex flex-wrap items-center gap-2 min-w-0">
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full border ${
                        course.grade_data.difficulty === 'easy'
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : course.grade_data.difficulty === 'moderate'
                            ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                            : course.grade_data.difficulty === 'challenging'
                              ? 'bg-orange-50 text-orange-700 border-orange-200'
                              : 'bg-red-50 text-red-700 border-red-200'
                      }`}
                    >
                      {course.grade_data.difficulty.charAt(0).toUpperCase() +
                        course.grade_data.difficulty.slice(1)}
                    </span>
                    {course.grade_data.average_gpa && (
                      <span className="text-ink-2 text-xs">
                        Avg GPA: {course.grade_data.average_gpa.toFixed(2)}
                      </span>
                    )}
                    {course.grade_data.total_students && (
                      <span className="text-ink-3 text-xs">
                        ({course.grade_data.total_students.toLocaleString()} students)
                      </span>
                    )}
                  </div>
                </>
              )}

              {showGradeUI && course.grade_data?.distribution && (
                <>
                  <div className="text-ink-2">Grades:</div>
                  <div className="col-span-1">
                    <GradeDistributionChart
                      distribution={course.grade_data.distribution as Record<string, number>}
                      totalStudents={course.grade_data.total_students ?? undefined}
                      averageGpa={course.grade_data.average_gpa ?? undefined}
                      variant="compact"
                    />
                  </div>
                </>
              )}

              {semesterValue &&
                semesterValue !== 'bookmarks' &&
                semesterValue !== '000008' &&
                !course.isPlaceholder &&
                checkAvailability && (
                  <>
                    <div className="text-ink-2">Availability:</div>
                    <div>
                      <CourseAvailabilityBadge
                        courseCode={course.code}
                        targetSemester={semesterValue}
                        checkAvailability={checkAvailability as never}
                        compact={false}
                        showWhenAvailable={true}
                      />
                    </div>
                  </>
                )}
            </div>
          </div>
        )}

        {isDragging && (
          <div className="absolute inset-0 bg-primary/10 rounded-card border-2 border-primary/30 border-dashed pointer-events-none" />
        )}
      </div>
    </>
  );
};

export default UltraCompactCard;
